/**
 * Curriculum model: the single source of truth for units, topics, and their
 * teaching order across the full 6th-grade year.
 *
 * Topics are aligned to the 2025 South Carolina College- and Career-Ready
 * Mathematics Standards for Grade 6. Each topic carries its SC standard code.
 * The topic order gives a full-year teaching sequence, which lets the app
 * derive "everything taught so far" from a single Current Topic pointer while
 * still allowing an explicit topicsTaught allow-list for finer control. Mixed
 * review draws only from taught topics.
 */

import type { SubskillId } from "./subskills";

export type UnitId = "unit1" | "unit2" | "unit3" | "unit4";

/** Stable topic identifiers - these also key the generator registry. */
export type TopicId =
  // Unit 1 - Numerical Reasoning
  | "equivalent-forms"
  | "compare-rationals"
  | "order-rationals"
  | "understand-integers"
  | "opposites"
  | "absolute-value"
  | "compare-order-integers"
  // Unit 2 - Patterns, Algebra & Functional Reasoning
  | "integer-operations"
  | "fraction-operations"
  | "decimal-operations"
  | "multiplicative-inverse"
  | "divide-fractions"
  | "additive-inverse"
  | "parts-expression"
  | "evaluate-expressions"
  | "exponent-expressions"
  | "equivalent-expressions"
  | "write-equations"
  | "write-inequalities"
  | "solve-equations"
  | "solve-inequalities"
  | "independent-dependent"
  | "ratios-rates"
  | "convert-units"
  // Unit 3 - Measurement, Geometry & Spatial Reasoning
  | "measuring-angles"
  | "angle-relationships"
  | "area-2d"
  | "area-composite"
  | "nets-3d"
  | "surface-area"
  | "volume-prisms"
  | "coordinate-plane"
  | "graphing-polygons"
  // Unit 4 - Data, Probability & Statistical Reasoning
  | "sample-size"
  | "measures-center-spread"
  | "box-plots"
  | "data-shape"
  | "likelihood"
  | "simple-probability"
  | "complement-probability";

export interface Unit {
  readonly id: UnitId;
  readonly name: string;
  readonly order: number;
}

export interface Topic {
  readonly id: TopicId;
  readonly unit: UnitId;
  readonly name: string;
  /** SC College- and Career-Ready standard code (e.g. "6.NR.1.1"). */
  readonly standard: string;
  /** Position in the overall teaching sequence (1-based, gapless). */
  readonly order: number;
  /** Subskills this topic characteristically exercises. */
  readonly subskills: readonly SubskillId[];
  /** True if the topic renders or reads graphs (affects question UI). */
  readonly requiresGraphing: boolean;
}

export const UNITS: readonly Unit[] = [
  { id: "unit1", name: "Numerical Reasoning", order: 1 },
  { id: "unit2", name: "Patterns, Algebra & Functional Reasoning", order: 2 },
  { id: "unit3", name: "Measurement, Geometry & Spatial Reasoning", order: 3 },
  { id: "unit4", name: "Data, Probability & Statistical Reasoning", order: 4 },
];

export const TOPICS: readonly Topic[] = [
  // --- Unit 1: Numerical Reasoning (1-7) --------------------------------
  {
    id: "equivalent-forms",
    unit: "unit1",
    name: "Equivalent Forms of Rational Numbers",
    standard: "6.NR.1.1",
    order: 1,
    subskills: ["equivalentForms", "fractionArithmetic", "decimalArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "compare-rationals",
    unit: "unit1",
    name: "Compare Positive Rational Numbers",
    standard: "6.NR.1.2",
    order: 2,
    subskills: ["placeValue", "equivalentForms"],
    requiresGraphing: false,
  },
  {
    id: "order-rationals",
    unit: "unit1",
    name: "Order Positive Rational Numbers",
    standard: "6.NR.1.3",
    order: 3,
    subskills: ["placeValue", "equivalentForms"],
    requiresGraphing: false,
  },
  {
    id: "understand-integers",
    unit: "unit1",
    name: "Understand Integers and Zero",
    standard: "6.NR.2.1",
    order: 4,
    subskills: ["numberLine", "signRules"],
    requiresGraphing: false,
  },
  {
    id: "opposites",
    unit: "unit1",
    name: "Opposites of Numbers",
    standard: "6.NR.2.2",
    order: 5,
    subskills: ["signRules", "numberLine"],
    requiresGraphing: false,
  },
  {
    id: "absolute-value",
    unit: "unit1",
    name: "Absolute Value",
    standard: "6.NR.2.3",
    order: 6,
    subskills: ["signRules", "numberLine"],
    requiresGraphing: false,
  },
  {
    id: "compare-order-integers",
    unit: "unit1",
    name: "Compare and Order Integers",
    standard: "6.NR.2.4",
    order: 7,
    subskills: ["signRules", "numberLine"],
    requiresGraphing: false,
  },

  // --- Unit 2: Patterns, Algebra & Functional Reasoning (8-24) ----------
  {
    id: "integer-operations",
    unit: "unit2",
    name: "Operations with Integers",
    standard: "6.PAFR.3.5",
    order: 8,
    subskills: ["integerArithmetic", "signRules"],
    requiresGraphing: false,
  },
  {
    id: "fraction-operations",
    unit: "unit2",
    name: "Operations with Fractions & Mixed Numbers",
    standard: "6.PAFR.3.6",
    order: 9,
    subskills: ["fractionArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "decimal-operations",
    unit: "unit2",
    name: "Operations with Decimals",
    standard: "6.PAFR.3.7",
    order: 10,
    subskills: ["decimalArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "multiplicative-inverse",
    unit: "unit2",
    name: "Multiplicative Inverse (Reciprocal)",
    standard: "6.PAFR.3.1",
    order: 11,
    subskills: ["reciprocals", "fractionArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "divide-fractions",
    unit: "unit2",
    name: "Divide Fractions Using Reciprocal",
    standard: "6.PAFR.3.2",
    order: 12,
    subskills: ["reciprocals", "fractionArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "additive-inverse",
    unit: "unit2",
    name: "Additive Inverse",
    standard: "6.PAFR.3.3",
    order: 13,
    subskills: ["signRules", "integerArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "parts-expression",
    unit: "unit2",
    name: "Parts of an Expression",
    standard: "6.PAFR.2.1",
    order: 14,
    subskills: ["variableRecognition"],
    requiresGraphing: false,
  },
  {
    id: "evaluate-expressions",
    unit: "unit2",
    name: "Write & Evaluate Algebraic Expressions",
    standard: "6.PAFR.2.2",
    order: 15,
    subskills: ["substitution", "orderOfOperations"],
    requiresGraphing: false,
  },
  {
    id: "exponent-expressions",
    unit: "unit2",
    name: "Numerical Expressions with Exponents",
    standard: "6.PAFR.2.3",
    order: 16,
    subskills: ["exponents", "orderOfOperations"],
    requiresGraphing: false,
  },
  {
    id: "equivalent-expressions",
    unit: "unit2",
    name: "Equivalent Expressions (Properties)",
    standard: "6.PAFR.3.4",
    order: 17,
    subskills: ["distributive", "propertiesOfOps"],
    requiresGraphing: false,
  },
  {
    id: "write-equations",
    unit: "unit2",
    name: "Write One-Step Equations",
    standard: "6.PAFR.1.1",
    order: 18,
    subskills: ["variableRecognition", "inverseOperations"],
    requiresGraphing: false,
  },
  {
    id: "write-inequalities",
    unit: "unit2",
    name: "Write One-Step Inequalities",
    standard: "6.PAFR.1.2",
    order: 19,
    subskills: ["variableRecognition", "inequalityReasoning"],
    requiresGraphing: false,
  },
  {
    id: "solve-equations",
    unit: "unit2",
    name: "Solve One-Step Equations",
    standard: "6.PAFR.1.3",
    order: 20,
    subskills: ["inverseOperations", "integerArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "solve-inequalities",
    unit: "unit2",
    name: "Solve One-Step Inequalities",
    standard: "6.PAFR.1.4",
    order: 21,
    subskills: ["inverseOperations", "inequalityReasoning"],
    requiresGraphing: true,
  },
  {
    id: "independent-dependent",
    unit: "unit2",
    name: "Independent & Dependent Variables",
    standard: "6.PAFR.2.4",
    order: 22,
    subskills: ["substitution", "variableRecognition"],
    requiresGraphing: false,
  },
  {
    id: "ratios-rates",
    unit: "unit2",
    name: "Ratios & Rates",
    standard: "6.PAFR.2.6",
    order: 23,
    subskills: ["ratioReasoning", "fractionArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "convert-units",
    unit: "unit2",
    name: "Convert Measurement Units",
    standard: "6.PAFR.2.7",
    order: 24,
    subskills: ["ratioReasoning"],
    requiresGraphing: false,
  },

  // --- Unit 3: Measurement, Geometry & Spatial Reasoning (25-33) --------
  {
    id: "measuring-angles",
    unit: "unit3",
    name: "Measuring Angles",
    standard: "6.MGSR.2.1",
    order: 25,
    subskills: ["angleRelationships"],
    requiresGraphing: false,
  },
  {
    id: "angle-relationships",
    unit: "unit3",
    name: "Complementary & Supplementary Angles",
    standard: "6.MGSR.2.2",
    order: 26,
    subskills: ["angleRelationships"],
    requiresGraphing: false,
  },
  {
    id: "area-2d",
    unit: "unit3",
    name: "Area of 2D Shapes",
    standard: "6.MGSR.1.1",
    order: 27,
    subskills: ["areaFormulas"],
    requiresGraphing: false,
  },
  {
    id: "area-composite",
    unit: "unit3",
    name: "Area of Composite Figures",
    standard: "6.MGSR.1.4",
    order: 28,
    subskills: ["areaFormulas"],
    requiresGraphing: false,
  },
  {
    id: "nets-3d",
    unit: "unit3",
    name: "Nets of 3D Shapes",
    standard: "6.MGSR.1.2",
    order: 29,
    subskills: ["surfaceAreaNets"],
    requiresGraphing: true,
  },
  {
    id: "surface-area",
    unit: "unit3",
    name: "Surface Area",
    standard: "6.MGSR.1.3",
    order: 30,
    subskills: ["surfaceAreaNets", "areaFormulas"],
    requiresGraphing: false,
  },
  {
    id: "volume-prisms",
    unit: "unit3",
    name: "Volume of Rectangular Prisms",
    standard: "6.MGSR.1.5",
    order: 31,
    subskills: ["volumeFormulas"],
    requiresGraphing: false,
  },
  {
    id: "coordinate-plane",
    unit: "unit3",
    name: "Plot & Identify Ordered Pairs (4 Quadrants)",
    standard: "6.MGSR.3.1",
    order: 32,
    subskills: ["coordinateGraphing"],
    requiresGraphing: true,
  },
  {
    id: "graphing-polygons",
    unit: "unit3",
    name: "Graph Polygons from Coordinates",
    standard: "6.MGSR.3.2",
    order: 33,
    subskills: ["coordinateGraphing"],
    requiresGraphing: true,
  },

  // --- Unit 4: Data, Probability & Statistical Reasoning (34-40) --------
  {
    id: "sample-size",
    unit: "unit4",
    name: "Sample Size",
    standard: "6.DPSR.1.1",
    order: 34,
    subskills: ["dataInterpretation"],
    requiresGraphing: false,
  },
  {
    id: "measures-center-spread",
    unit: "unit4",
    name: "Median, Mode, Range, IQR",
    standard: "6.DPSR.1.4",
    order: 35,
    subskills: ["statisticalMeasures"],
    requiresGraphing: false,
  },
  {
    id: "box-plots",
    unit: "unit4",
    name: "Create & Interpret Box Plots",
    standard: "6.DPSR.1.2",
    order: 36,
    subskills: ["statisticalMeasures", "dataInterpretation"],
    requiresGraphing: true,
  },
  {
    id: "data-shape",
    unit: "unit4",
    name: "Shape of Data (Skew, Symmetric, Outliers)",
    standard: "6.DPSR.1.3",
    order: 37,
    subskills: ["dataInterpretation", "statisticalMeasures"],
    requiresGraphing: false,
  },
  {
    id: "likelihood",
    unit: "unit4",
    name: "Likelihood of Events",
    standard: "6.DPSR.2.1",
    order: 38,
    subskills: ["probabilityBasics"],
    requiresGraphing: false,
  },
  {
    id: "simple-probability",
    unit: "unit4",
    name: "Probability of Simple Events",
    standard: "6.DPSR.2.2",
    order: 39,
    subskills: ["probabilityBasics", "fractionArithmetic"],
    requiresGraphing: false,
  },
  {
    id: "complement-probability",
    unit: "unit4",
    name: "Complement of an Event",
    standard: "6.DPSR.2.3",
    order: 40,
    subskills: ["probabilityBasics"],
    requiresGraphing: false,
  },
];

// --- Lookups -------------------------------------------------------------

const TOPIC_BY_ID = new Map<TopicId, Topic>(TOPICS.map((t) => [t.id, t]));
const UNIT_BY_ID = new Map<UnitId, Unit>(UNITS.map((u) => [u.id, u]));

export function getTopic(id: TopicId): Topic {
  const t = TOPIC_BY_ID.get(id);
  if (!t) throw new Error(`unknown topic: ${id}`);
  return t;
}

export function getUnit(id: UnitId): Unit {
  const u = UNIT_BY_ID.get(id);
  if (!u) throw new Error(`unknown unit: ${id}`);
  return u;
}

export function topicsInUnit(unit: UnitId): readonly Topic[] {
  return TOPICS.filter((t) => t.unit === unit).sort((a, b) => a.order - b.order);
}

export function isTopicId(x: string): x is TopicId {
  return TOPIC_BY_ID.has(x as TopicId);
}

/**
 * Everything taught up to and including the given topic, by teaching order.
 * Useful as a default topicsTaught allow-list derived from a single Current
 * Topic pointer.
 */
export function topicsUpToAndIncluding(id: TopicId): readonly Topic[] {
  const cutoff = getTopic(id).order;
  return TOPICS.filter((t) => t.order <= cutoff).sort(
    (a, b) => a.order - b.order,
  );
}

// --- Taught allow-list ---------------------------------------------------

/**
 * Which topics are available for practice/review. Explicit set of taught
 * topics; the current topic is always considered taught. Mixed review draws
 * only from this set.
 */
export class TaughtSet {
  private readonly taught: Set<TopicId>;

  constructor(taught: Iterable<TopicId> = []) {
    this.taught = new Set(taught);
  }

  /** Build from a Current Topic pointer, marking everything up to it taught. */
  static upTo(current: TopicId): TaughtSet {
    return new TaughtSet(topicsUpToAndIncluding(current).map((t) => t.id));
  }

  has(id: TopicId): boolean {
    return this.taught.has(id);
  }

  add(id: TopicId): void {
    this.taught.add(id);
  }

  remove(id: TopicId): void {
    this.taught.delete(id);
  }

  /** All taught topics, in teaching order. */
  list(): readonly Topic[] {
    return TOPICS.filter((t) => this.taught.has(t.id)).sort(
      (a, b) => a.order - b.order,
    );
  }

  /** Taught topics within a unit, in teaching order. */
  listInUnit(unit: UnitId): readonly Topic[] {
    return this.list().filter((t) => t.unit === unit);
  }

  ids(): readonly TopicId[] {
    return this.list().map((t) => t.id);
  }
}
